import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { useEffect, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, RefreshCw, SearchX } from "lucide-react";
import type { IClient } from "src/services/ClientService";
import type { ClientListPage } from "src/services/clientListData";
import type { ClientFinanceAccess, ClientListSort } from "src/services/clientListRequest";
import { ClientPanel, ClientRow } from "./ClientPortfolio.styles";
import { formatClientConversion, formatClientDate, getClientActivity, getClientConversionColor } from "./clientFinancePresentation";
import { PortfolioCopyId, PortfolioStatus } from "./PortfolioIdentity";

export const clientSortOptions: { value: ClientListSort; name: string }[] = [
	{ value: "created_at", get name() { return msg("created"); } }, { value: "name", get name() { return msg("name"); } }, { value: "status", get name() { return msg("status"); } },
	{ value: "finance_attempts", get name() { return msg("attempts"); } }, { value: "finance_conversion", get name() { return msg("conversion"); } },
	{ value: "finance_turnover", get name() { return msg("turnover_8ecaf1d7"); } }, { value: "finance_profit", get name() { return msg("profit"); } },
	{ value: "finance_last_activity", get name() { return msg("lastActivity"); } },
];

function PortfolioRow({ client, financeAccess, onOpen, renderReport, now }: {
	client: IClient; financeAccess: ClientFinanceAccess; onOpen: (client: IClient) => void;
	renderReport?: (client: IClient) => ReactNode; now: number;
}) {
	useLocale();
	const [expanded, setExpanded] = useState(false);
	const finance = financeAccess !== "none" ? client.finance : undefined;
	const activity = getClientActivity(finance, now);
	const conversion = finance?.traffic.conversion;
	const hasConversion = conversion != null && Number.isFinite(conversion);
	const countryCode = client.country?.trim().toUpperCase() ?? "";
	const region = countryCode === "UK" ? "GB" : countryCode;
	const countryName = /^[A-Z]{2}$/.test(region) ? new Intl.DisplayNames(["en"], { type: "region" }).of(region) : client.country;
	const registrationNumber = client.registration_number?.trim();
	const payout = client.payout;
	const payoutPeriod = payout?.period ? payout.period.charAt(0).toUpperCase() + payout.period.slice(1) : "";
	const statementDay = payout?.statement_day ? payout.statement_day.charAt(0).toUpperCase() + payout.statement_day.slice(1) : "—";
	const payoutLabel = payout?.period === "daily"
		? payout.cut_off_day != null ? `T+${payout.cut_off_day}` : "—"
		: payout ? `${payoutPeriod || "—"} - ${statementDay}` : "—";
	const canOpenReport = financeAccess === "full" && !!renderReport;
	return <ClientRow $color="#667085" $background="#f2f4f7" $barColor="transparent" data-expanded={expanded && canOpenReport}>
		<div className="client-grid" onClick={() => onOpen(client)}>
			<span className="country" title={countryName}>{/^[A-Z]{2}$/.test(region) ? <img className="country-flag" src={`https://flagcdn.com/${region.toLowerCase()}.svg`} alt={countryName} loading="lazy" /> : "—"}</span>
			<div className="client-identity">
				<div className="subject-title">
					<button type="button" className="client-name" onClick={event => { event.stopPropagation(); onOpen(client); }}>{client.name}</button>
					<PortfolioStatus status={client.status} />
				</div>
				<span className="description">{countryName || "—"}{registrationNumber ? `, ${registrationNumber}` : ""}</span>
			</div>
			<div className="client-meta">
				<div className="client-created">{formatClientDate(client.created_at)}</div>
				<div className="client-id"><PortfolioCopyId id={client.id} entity="client" /></div>
			</div>
			<div className="client-payout" title={payout?.activated_from ? msg("templateActiveFrom_676a5745", { v0: formatClientDate(payout.activated_from) }) : undefined}>
				<span className={payoutLabel === "—" ? "payout-empty" : "payout-badge"}>{payoutLabel}</span>
			</div>
			<div className="client-value conversion" title={msg("completedAllAttemptsIncludesAllSessionTypesAndSandboxTraffic")}>
				{hasConversion ? <span className="conversion-number" style={{ color: getClientConversionColor(conversion) }}>{formatClientConversion(finance)}</span> : <span>—</span>}
			</div>
			<div className="client-value activity" data-inactive={activity.inactive} title={finance?.traffic.last_activity_at ? `${finance.traffic.last_activity_at} UTC` : undefined}>{activity.label}</div>
			<div className="row-actions" onClick={event => event.stopPropagation()}>
				{canOpenReport && <button type="button" className="icon-button" title={expanded ? msg("hideFinancialReport") : msg("showFinancialReport")} aria-label={msg("templateFinancialReportFor_7d9b5ae4", { v0: expanded ? msg("hide") : msg("show"), v1: client.name })} aria-expanded={expanded} aria-controls={`client-report-${client.id}`} onClick={() => setExpanded(value => !value)}>{expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</button>}
			</div>
		</div>
		{expanded && canOpenReport && <section className="expanded-report" id={`client-report-${client.id}`} aria-label={msg("templateFinancialReportFor_c4b03645", { v0: client.name })}>{renderReport!(client)}</section>}
	</ClientRow>;
}

export interface ClientPortfolioProps {
	data?: ClientListPage; busy: boolean; error?: string; financeAccess: ClientFinanceAccess;
	sort: ClientListSort; sortDir: "asc" | "desc";
	onSort: (sort: ClientListSort) => void; onDirection: () => void; onRetry: () => void;
	onOpen: (client: IClient) => void;
	renderReport?: (client: IClient) => ReactNode; footer?: ReactNode;
}

export function ClientPortfolio({ data, busy, error, financeAccess, sort, sortDir, onSort, onDirection, onRetry, onOpen, renderReport, footer }: ClientPortfolioProps) {
	useLocale();
	const finance = financeAccess !== "none" ? data?.finance : undefined;
	const [now, setNow] = useState(Date.now);
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 60000);
		return () => clearInterval(timer);
	}, []);
	const SortIcon = sortDir === "desc" ? ArrowDown : ArrowUp;
	const heading = (label: string, field: ClientListSort) => <span><button type="button" data-selected={sort === field} onClick={() => sort === field ? onDirection() : onSort(field)} aria-label={msg("templateSortBy_bc9ca716", { v0: label, v1: sort === field ? (sortDir === "asc" ? msg("clientSortAscending") : msg("clientSortDescending")) : "" })}>{label}{sort === field && <SortIcon size={12} />}</button></span>;
	return <ClientPanel $financeAccess={financeAccess} aria-label={msg("clientPortfolio")} aria-busy={busy}>
		{!!finance?.degraded.length && <p role="status" className="notice">{msg("someFinancialDataIsIncomplete")}</p>}
		{data?.truncated && <p role="status" className="notice">{msg("resultsAreLimitedByTheServerNarrowYourSearch")}</p>}
		<div className="client-list-scroll" role="region" aria-label={msg("clientsList")} tabIndex={0}>
		<div className="column-head">
			<span />{heading(msg("client"), "name")}
			{heading(msg("created"), "created_at")}
			<span>{msg("payout")}</span>
			{financeAccess !== "none" ? <>{heading(msg("conversion"), "finance_conversion")}{heading(msg("lastActivity"), "finance_last_activity")}</> : <><span>{msg("conversion")}</span><span>{msg("lastActivity")}</span></>}
			<span />
		</div>
		{busy ? <div className="state" role="status"><RefreshCw size={22} /><strong>{msg("loadingClients")}</strong></div>
			: error ? <div className="state" role="alert"><strong>{msg("couldNotLoadClients")}</strong><span>{error}</span><button type="button" onClick={onRetry}>{msg("tryAgain")}</button></div>
				: !data?.items.length ? <div className="state" role="status"><SearchX size={26} /><strong>{msg("noClientsFound")}</strong><span>{msg("tryAnotherNameOrChangeTheStatusFilter")}</span></div>
					: data.items.map(client => <PortfolioRow key={client.id} client={client} financeAccess={financeAccess} onOpen={onOpen} renderReport={renderReport} now={now} />)}
		</div>
		{footer && <div className="portfolio-footer">{footer}</div>}
		{finance && <p className="finance-note">{msg("period_8ac7d1d6")}{formatClientDate(finance.period.from)} – {formatClientDate(finance.period.to)} UTC.
			{financeAccess === "full" && <>{msg("moneyBySettlementDate")}{finance.money_complete_through ? msg("templateCompleteThrough_b74a97b9", { v0: formatClientDate(finance.money_complete_through) }) : ""}{msg("currenciesShownSeparately")}</>}
		</p>}
	</ClientPanel>;
}
